import { useEffect, useRef, useState } from 'react';
import styles from './TrendChart.module.css';

// 見通し章「これからの見通し」用の小さな折れ線（純SVG・ライブラリなし）。
// ブランド規律: 墨1.5px の線と点のみ。グリッド・塗り・アニメーションは入れない。
// 値ラベルは最初と最後の点だけに出し、途中の点は線の形で読ませる。
// コンテナ幅を ResizeObserver で計測し、viewBox をピクセル等倍にして他コンテンツ
// (ChapterMap 等)と同じ幅いっぱいに描く — viewBox 固定のまま引き伸ばすと文字と線が
// 一緒に拡大されてしまうため。
// 座標計算は computeTrendLayout に分離してあり、PDF 冊子 (pdf/) も同じ関数で同じ形の折れ線を描く。

export type TrendPoint = { label: string; value: number };

type Props = {
  points: TrendPoint[];
  height?: number;
  formatValue?: (v: number) => string;
  /** 読み上げ文（例: 「2025年 約1,200人 から 2050年 約950人 へ」）。 */
  ariaLabel: string;
};

const PAD_X = 40;
const PAD_TOP = 20;
const PAD_BOTTOM = 22;
// 計測完了前の初期描画に使う仮幅。ResizeObserver は observe 直後に発火するため一瞬で実幅に揃う。
const FALLBACK_W = 640;

export type TrendLabel = { x: number; y: number; anchor: 'start' | 'end'; text: string };

export type TrendLayout = {
  width: number;
  height: number;
  /** 折れ線の各点 (px)。 */
  points: { x: number; y: number }[];
  axisY: number;
  firstValue: TrendLabel;
  lastValue: TrendLabel;
  firstYear: TrendLabel;
  lastYear: TrendLabel;
};

export const defaultFormatValue = (v: number): string => v.toLocaleString('ja-JP');

/** 折れ線の座標を計算する (点が 2 未満なら null)。画面 SVG と PDF SVG が共有する。 */
export function computeTrendLayout(
  points: TrendPoint[],
  width: number,
  height: number,
  formatValue: (v: number) => string = defaultFormatValue,
): TrendLayout | null {
  if (points.length < 2) return null;
  const W = width;
  const innerW = W - PAD_X * 2;
  const innerH = height - PAD_TOP - PAD_BOTTOM;
  const values = points.map((p) => p.value);
  const min = Math.min(...values);
  const max = Math.max(...values);
  // 全点同値でも線が中央に描けるよう、レンジを僅かに広げる。
  const span = max - min || Math.max(Math.abs(max), 1) * 0.1;
  const lo = min - span * 0.08;
  const hi = max + span * 0.08;

  const x = (i: number) => PAD_X + (innerW * i) / (points.length - 1);
  const y = (v: number) => PAD_TOP + innerH * (1 - (v - lo) / (hi - lo));

  const first = points[0];
  const last = points[points.length - 1];
  const axisY = height - PAD_BOTTOM + 6;

  return {
    width: W,
    height,
    points: points.map((p, i) => ({ x: x(i), y: y(p.value) })),
    axisY,
    // 値ラベルはチャートの左右端に寄せ、折れ線・点との重なりを避ける。
    firstValue: { x: 2, y: Math.max(10, y(first.value) - 8), anchor: 'start', text: formatValue(first.value) },
    lastValue: { x: W - 2, y: Math.max(10, y(last.value) - 8), anchor: 'end', text: formatValue(last.value) },
    firstYear: { x: x(0), y: height - 6, anchor: 'start', text: first.label },
    lastYear: { x: x(points.length - 1), y: height - 6, anchor: 'end', text: last.label },
  };
}

export default function TrendChart({
  points,
  height = 120,
  formatValue,
  ariaLabel,
}: Props) {
  const wrapRef = useRef<HTMLDivElement>(null);
  const [measuredW, setMeasuredW] = useState(0);

  useEffect(() => {
    const el = wrapRef.current;
    if (!el) return;
    const observer = new ResizeObserver((entries) => {
      const width = entries[0]?.contentRect.width;
      if (width) setMeasuredW(Math.round(width));
    });
    observer.observe(el);
    return () => observer.disconnect();
  }, []);

  const layout = computeTrendLayout(points, measuredW || FALLBACK_W, height, formatValue);
  if (!layout) return null;
  const { width: W, axisY, firstValue, lastValue, firstYear, lastYear } = layout;

  return (
    <div ref={wrapRef} className={styles.wrap}>
      <svg
        className={styles.chart}
        viewBox={`0 0 ${W} ${height}`}
        width="100%"
        height={height}
        role="img"
        aria-label={ariaLabel}
      >
        <line className={styles.axis} x1={PAD_X} y1={axisY} x2={W - PAD_X} y2={axisY} />
        <polyline
          className={styles.line}
          points={layout.points.map((p) => `${p.x},${p.y}`).join(' ')}
        />
        {layout.points.map((p, i) => (
          <circle key={i} className={styles.dot} cx={p.x} cy={p.y} r={2.5} />
        ))}
        <text className={styles.valueLabel} x={firstValue.x} y={firstValue.y} textAnchor={firstValue.anchor}>
          {firstValue.text}
        </text>
        <text className={styles.valueLabel} x={lastValue.x} y={lastValue.y} textAnchor={lastValue.anchor}>
          {lastValue.text}
        </text>
        <text className={styles.yearLabel} x={firstYear.x} y={firstYear.y} textAnchor={firstYear.anchor}>
          {firstYear.text}
        </text>
        <text className={styles.yearLabel} x={lastYear.x} y={lastYear.y} textAnchor={lastYear.anchor}>
          {lastYear.text}
        </text>
      </svg>
    </div>
  );
}
